import React, { useEffect, useRef, useState } from "react";
import { ReactComponent as MDLogo } from "../../assets/math-logo.svg";
import { ReactComponent as EDLogo } from "../../assets/english-logo.svg";
import { ReactComponent as TDLogo } from "../../assets/logos/Td-logo.svg";
import { ReactComponent as SDLogo } from "../../assets/science-logo.svg";
import "./navbar.css";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../../store/main";
import { initProfile } from "../../store/setProfile";
import { API_URL, createAxiosInstance } from "../../api";

import config from "config";
import { getAvatarUrl } from '../../utils/imageUtils';
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Badge,
  Box,
  Button,
  Link as MuiLink,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import { 
  KeyboardArrowDown,
  Dashboard as DashboardIcon,
  Person as PersonIcon,
  School as SchoolIcon,
  Message as MessageIcon,
  CreditCard as CreditCardIcon,
  Logout as LogoutIcon,
  HowToReg as OnboardingIcon
} from "@mui/icons-material";
import sd from "../../assets/sciencedirectory-logo/sciencedirectory-logo-icon.png";
import ed from "../../assets/englishdirectory-logo/englishdirectory-20240318-logo-icon.png";
import md from "../../assets/logos/md-logo-icon.png";
import td from "../../assets/logos/Td-logo.svg";
import { isProfileCompleted } from "../../utils";
const Navbar = () => {
  // State for loading and userId
  const [isLoading, setIsLoading] = useState(false); // Start with false since we're using local state
  const [localUserId, setLocalUserId] = useState(null);
  const profileUser = useSelector((state) => state.profile?.user);
  const profileUserId = profileUser?._id;
  // Get userId from Redux first (preferred source)
  const reduxUserId = useSelector((state) => state.main.userId);
  let Logo = MDLogo;
  if (config["class-name"] === "english") {
    Logo = EDLogo;
  }
  if (config["class-name"] === "science") {
    Logo = SDLogo;
  }
  if (config["class-name"] === "tutors") {
    Logo = TDLogo;
  }
  const parentsSignUpStyle = {
    variant: "outlined",
    color: "secondary",
  };

  if (config["class-name"] === "math") {
    parentsSignUpStyle.color = "success";
    parentsSignUpStyle.variant = "contained";
  }

  const dispatch = useDispatch();
  const user = useSelector((state) => state.main);
  const isAuth = useSelector((state) => !!state.main.userId);
  const { firstName, lastName, isGuest } = useSelector((state) => state.main);
  const { avatar: tutorAvatar, gender } = useSelector(
    (state) => state.profile.user
  );
  const { _id } = useSelector((state) => state.profile.guestInformation);
  const { avatar: guestAvatar } = useSelector(
    (state) => state.profile.guestInformation
  );

  const unreadMessageCount = useSelector(
    (state) =>
      state.profile?.messages?.filter((message) => !message.isRead).length
  );
  const fullName = isAuth
    ? `${firstName.charAt(0).toUpperCase() + firstName.slice(1)} ${
        lastName.length > 0 ? lastName.charAt(0).toUpperCase() + "." : ""
      }`
    : "";
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const handleClickOtherDirectory = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const avatar =
    isGuest && !guestAvatar?.includes("temp") ? guestAvatar : tutorAvatar;
  const userAvatar = getAvatarUrl(avatar, gender);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const profileMenuRef = useRef(null);

  const sixtyDaysLater = user.createdAt + 5184000000;
  const currentDate = Date.now();
  const isExpired = sixtyDaysLater < currentDate;

  const navigate = useNavigate();
  const location = useLocation();
  const handleFindTutor = () => {
    if (location.pathname === "/search") {
      window.location.reload();
    } else {
      navigate("/search");
    }
  };
  const [billingStatus, setBillingStatus] = useState("false"); // Local state for billing status
  //const userId = useSelector((state) => state.profile.user.user);

  // Get userId from localStorage only if not available in Redux
  useEffect(() => {
    if (!reduxUserId) {
      setIsLoading(true);
      try {
        const persistedData = localStorage.getItem("persist:root");
        if (persistedData) {
          const parsedData = JSON.parse(persistedData);
          const rawUserId = parsedData?.userId;

          if (rawUserId) {
            // Clean the userId string safely
            const cleanedUserId = String(rawUserId)
              .replace(/\\/g, "")
              .replace(/"/g, "")
              .trim();

            if (cleanedUserId) {
              setLocalUserId(cleanedUserId);
            }
          }
        }
      } catch (error) {
        console.error("Error parsing localStorage userId:", error);
      } finally {
        setIsLoading(false);
      }
    }
  }, [reduxUserId]);

  // Determine which userId to use
  const userId = reduxUserId || localUserId;

  useEffect(() => {
    // Handle checking the payment status
    const checkPaymentStatus = async () => {
      if (!userId) {
        console.log("User ID is undefined or invalid. Skipping payment check.");
        return; // Exit the function early if the userId is not valid
      }

      try {
        const axiosInstance = createAxiosInstance();
        const response = await axiosInstance.post(
          `${API_URL}/api/billings/hasPaid/${userId}`
        );

        if (response.data && response.data.paid) {
          setBillingStatus(response.data.paid);
        } else {

          setBillingStatus("false");
        }
      } catch (error) {
        console.error(
          "Error checking payment status:",
          error.response?.data?.message || error.message
        );
        setBillingStatus("false");
      }
    };

    checkPaymentStatus();

    // Handle event listener for clicking outside the profile menu
    const handleOutsideClick = (event) => {
      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(event.target)
      ) {
        setShowProfileMenu(false);
      }
    };

    // Attach event listener to document body
    document.body.addEventListener("click", handleOutsideClick);

    // Cleanup event listener on component unmount
    return () => {
      document.body.removeEventListener("click", handleOutsideClick);
    };
  }, [userId]); // Re-run the effect when `userId` changes

  const handleClick = () => {
    setShowProfileMenu(!showProfileMenu);
  };

  const logoutHandle = () => {
    dispatch(logout());
    dispatch(initProfile());
  };

  const linkStyle = {
    textDecoration: "none",
    color: "inherit",
  };

  if (isLoading) {
    return (
      <div className="navbar-loading">
        <div className="logo-placeholder" />
        <div className="loading-spinner" />
      </div>
    );
  }

  return (
    <div className="navbar" id="navbar">
      <div className="logo" id="logo">
        <Link to="/">
          <Logo style={{ height: "75px" }} />
        </Link>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
        <ul className="nav-container">
          <li className={"hidden-md"}>
            <Link className={`nav-item link ${location.pathname === '/' ? 'active' : ''}`} to="/">
              Home
            </Link>
          </li>
          <li className={"hidden-md"}>
            <Link className={`nav-item link ${location.pathname.startsWith('/howitworks') ? 'active' : ''}`} to="/howitworks">
              How it works
            </Link>
          </li>

          <li className={"hidden-md"}>
            <Link className={`nav-item link ${location.pathname.startsWith('/courses') || location.pathname.startsWith('/classes') ? 'active' : ''}`} to="/courses">
              Courses/classes
            </Link>
          </li>

          {/* <li className={'hidden-md'}>
          <span className="nav-item link" style={{cursor: "pointer"}} onClick={() => {
            handleFindTutor()
          }}>
            Home
          </span>
          </li> */}
          <li className={"hidden-md"}>
            <Link
              className={`nav-item link ${location.pathname.startsWith('/search') ? 'active' : ''}`}
              to="/search"
              style={{ cursor: "pointer" }}
            >
              {config["navbar-link"]}
            </Link>
          </li>
          <li className={"hidden-md"}>
            <Link 
              className={`nav-item link contact-btn ${location.pathname.startsWith('/contact') ? 'active' : ''}`} 
              to="/contact"
            >
              Contact us
            </Link>
          </li>
          {isAuth ? (
            <li
              className="profile explore-more"
              onClick={handleClick}
              ref={profileMenuRef}
            >
              <span className="name">
                <strong>Nicholas teacher</strong>
              </span>
              <Badge
                className="avatar-bg"
                badgeContent={
                  unreadMessageCount > 0 ? unreadMessageCount : null
                }
                color="error"
                overlap="circular"
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
              >
                <div
                  className="avatar"
                  style={{
                    backgroundImage: `url(${userAvatar})`,
                    display: "inline-block",
                    justifyContent: "flex-start",
                    border: unreadMessageCount ? "2px solid #f00" : "none",
                    width: "50px", 
                    height: "50px",
                    borderRadius: "50%",
                  }}
                ></div>
              </Badge>

              {showProfileMenu && (
                <div className="profile-menu">
                  <div className="profile-menu-header">
                    <div className="profile-avatar">
                      {userAvatar && userAvatar !== '/assets/image-placeholder.png' ? (
                        <div
                          className="avatar-small"
                          style={{
                            backgroundImage: `url(${userAvatar})`,
                          }}
                        ></div>
                      ) : (
                        <div className="avatar-small avatar-initials">
                          {fullName.split(' ').map(name => name.charAt(0)).join('').toUpperCase()}
                        </div>
                      )}
                    </div>
                    <div className="profile-info">
                      <div className="profile-name">{fullName}</div>
                      <Tooltip title={user.email || 'User'} placement="top">
                        <div className="profile-email">
                          {user.email || 'User'}
                        </div>
                      </Tooltip>
                    </div>
                  </div>
                  
                  
                  <div className="profile-menu-items">
                    <Link to="/dashboard" className="profile-menu-item">
                      <DashboardIcon className="menu-icon" />
                      <span>Dashboard</span>
                    </Link>
                    
                    {isAuth && !isGuest && (
                      <Link to="/profile?step=0" className="profile-menu-item">
                        <PersonIcon className="menu-icon" />
                        <span>{!_id ? "Create Tutor profile" : "Tutor profile"}</span>
                      </Link>
                    )}

                    {isAuth && !isGuest && (
                      <Link to="/onboarding-v2" className="profile-menu-item">
                        <OnboardingIcon className="menu-icon" />
                        <span>Onboarding V2 (Test)</span>
                      </Link>
                    )}

                    {isAuth && !isGuest && (
                      <Link to="/my-courses" className="profile-menu-item">
                        <SchoolIcon className="menu-icon" />
                        <span>Add/Edit Course/Classes</span>
                      </Link>
                    )}

                    <Link to="/messages" className="profile-menu-item profile-menu-item-with-badge">
                      <div className="menu-item-content">
                        <MessageIcon className="menu-icon" />
                        <span>Messages</span>
                      </div>
                      {unreadMessageCount > 0 && (
                        <Badge
                          badgeContent={unreadMessageCount}
                          color="error"
                        />
                      )}
                    </Link>
                    
                    {user.isGuest == false && billingStatus !== "false" && (
                      <Link to="/billing" className="profile-menu-item">
                        <CreditCardIcon className="menu-icon" />
                        <span>Subscription</span>
                      </Link>
                    )}
                    
                    <Link
                      to="/"
                      onClick={() => {
                        logoutHandle();
                      }}
                      className="profile-menu-item logout-item"
                    >
                      <LogoutIcon className="menu-icon" />
                      <span>Logout</span>
                    </Link>
                  </div>
                </div>
              )}
            </li>
          ) : (
            <li className="auth-li">
              <div className="auth-group">
                <Link
                  className={`auth-btn ${location.pathname.startsWith('/signin') ? 'active' : ''}`}
                  to="/signin"
                >
                  Sign in
                </Link>
                <Link
                  className={`auth-btn ${!location.pathname.startsWith('/signin') ? 'active' : ''}`}
                  to="/signup?tutor"
                >
                  Sign Up
                </Link>
              </div>
            </li>
          )}
        </ul>
        {/* Mobile menu  */}
        <div className={"right " + (menuOpen && "active")}>
          <div
            className="hamburger explore-more-mobile "
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <span className="line1"></span>
            <span className="line2"></span>
            <span className="line3"></span>
          </div>
        </div>
      </div>

      <div className={"menu " + (menuOpen && "active")}>
        <ul>
          {isAuth && (
            <li className="full-name">
              <Link
                className="nav-item link"
                to="/dashboard"
                onClick={() => setMenuOpen(!menuOpen)}
                style={{ display: "flex", alignItems: "center", gap: "1rem" }}
              >
                <div
                  className="avatar"
                  style={{
                    backgroundImage: `url(${
                      userAvatar
                    })`,
                    display: "inline-block",
                    justifyContent: "flex-start",
                    border: unreadMessageCount ? "2px solid #f00" : "none",
                  }}
                ></div>
                {fullName}
              </Link>
            </li>
          )}
          <li>
            <Link
              className="nav-item link"
              to="/"
              onClick={() => setMenuOpen(!menuOpen)}
            >
              Home
            </Link>
          </li>
          <li>
            <Link
              className="nav-item link"
              to="/howitworks"
              onClick={() => setMenuOpen(!menuOpen)}
            >
              How it works
            </Link>
          </li>
          {isAuth && (
            <>
              <li>
                <Link
                  className="nav-item link"
                  to="/dashboard"
                  onClick={() => setMenuOpen(!menuOpen)}
                  style={{ color: "#712d94" }}
                >
                  Dashboard
                </Link>
              </li>
              <li>
                <Link
                  className={`nav-item link ${unreadMessageCount > 0 ? 'has-badge' : ''}`}
                  to="/messages"
                  onClick={() => setMenuOpen(!menuOpen)}
                >
                  Messages
                  {unreadMessageCount > 0 && (
                    <Badge
                      badgeContent={unreadMessageCount}
                      color="error"
                      className="side-message-badge"
                    />
                  )}
                </Link>
              </li>
              {/*<li>*/}
              {/*  <Link*/}
              {/*    className="nav-item"*/}
              {/*    to="/billing"*/}
              {/*    onClick={() => setMenuOpen(!menuOpen)}*/}
              {/*  >*/}
              {/*    <span>Subscription</span>*/}
              {/*  </Link>*/}
              {/*</li>*/}
            </>
          )}

          {isAuth && !isGuest && (
            <li>
              <Link
                onClick={() => setMenuOpen(!menuOpen)}
                to="/profile?step=0"
                className={!_id ? "nav-item link" : "nav-item link"}
              >
                {!_id ? "Create Tutor profile" : "Tutor profile"}
              </Link>
            </li>
          )}
          <li>
            <Link
              className="nav-item link"
              to="/search"
              onClick={() => setMenuOpen(!menuOpen)}
            >
              {config["navbar-link"]}
            </Link>
          </li>
          <li>
            <Link className="nav-item link" to="/courses">
              Courses/classes
            </Link>
          </li>
          {isAuth && user.isGuest == false && billingStatus !== "false" && (
            <li>
              <Link
                className="nav-item link"
                to="/billing"
                onClick={() => setMenuOpen(!menuOpen)}
              >
                Subscription
              </Link>
            </li>
          )}
          <li>
            <Link
              className="nav-item link"
              to="/contact"
              onClick={() => setMenuOpen(!menuOpen)}
            >
              Contact us
            </Link>
          </li>
        </ul>
        <ul>
          {isAuth ? (
            <li>
              <Link
                className="nav-item link"
                to="/"
                onClick={() => {
                  setMenuOpen(!menuOpen);
                  logoutHandle();
                }}
              >
                Log out
              </Link>
            </li>
          ) : (
            <>
              <li>
                <Link
                  className="nav-item link"
                  to="/signup?tutor"
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  Sign Up
                  {/*<Box*/}
                  {/*  style={{*/}
                  {/*    display: "flex",*/}
                  {/*    justifyContent: "space-between",*/}
                  {/*    alignItems: "center",*/}
                  {/*  }}*/}
                  {/*>*/}
                  {/*  <Chip*/}
                  {/*    label="It's Free!"*/}
                  {/*    color="warning"*/}
                  {/*    sx={{*/}
                  {/*      backgroundColor: config["chip-colour"],*/}
                  {/*      p: "2px",*/}
                  {/*      boxShadow: 1,*/}
                  {/*      borderRadius: "2px",*/}
                  {/*    }}*/}
                  {/*    size="small"*/}
                  {/*    variant="filled"*/}
                  {/*  />*/}
                  {/*</Box>*/}
                </Link>
              </li>

              <li>
                <Link
                  className="nav-item link"
                  to="/signin"
                  onClick={() => {
                    setMenuOpen(!menuOpen);
                  }}
                >
                  Sign in
                </Link>
              </li>
            </>
          )}
        </ul>
      </div>
    </div>
  );
};

export default Navbar;
